/**
 * Mirrors of the backend enums in `src/enums/`.
 *
 * Kept as `const` objects rather than TS `enum`s so the values erase cleanly at
 * runtime and can be iterated for filter dropdowns. If the backend adds a
 * value, add it here — the union types below will surface every switch that
 * needs updating.
 */

export const SocialPlatform = {
  FACEBOOK: 'FACEBOOK',
  INSTAGRAM: 'INSTAGRAM',
  LINKEDIN: 'LINKEDIN',
  X: 'X',
  TELEGRAM: 'TELEGRAM',
  TIKTOK: 'TIKTOK',
  YOUTUBE: 'YOUTUBE',
  WHATSAPP: 'WHATSAPP',
} as const;
export type SocialPlatform =
  (typeof SocialPlatform)[keyof typeof SocialPlatform];

/** Platforms that can actually be connected and posted to today. */
export const SUPPORTED_PLATFORMS: SocialPlatform[] = [
  SocialPlatform.FACEBOOK,
  SocialPlatform.INSTAGRAM,
  SocialPlatform.LINKEDIN,
  SocialPlatform.X,
  SocialPlatform.TELEGRAM,
  SocialPlatform.TIKTOK,
  SocialPlatform.YOUTUBE,
];

/**
 * Platforms a blog, or any publish that names no accounts, fans out to.
 *
 * Mirrors `FANOUT_PLATFORMS` on the backend. YouTube is left out: a YouTube
 * upload is a video with its own title, audience and licence, made in the
 * YouTube section or by picking a channel in the composer — never something a
 * blog distribution or "select all" should reach by accident.
 */
export const FANOUT_PLATFORMS: SocialPlatform[] = SUPPORTED_PLATFORMS.filter(
  (platform) => platform !== SocialPlatform.YOUTUBE
);

/** Platforms reachable through an OAuth "Connect" button. */
export const OAUTH_PLATFORMS: SocialPlatform[] = [
  SocialPlatform.FACEBOOK,
  SocialPlatform.INSTAGRAM,
  SocialPlatform.LINKEDIN,
  SocialPlatform.X,
  SocialPlatform.TIKTOK,
  SocialPlatform.YOUTUBE,
];

export const BlogStatus = {
  DRAFT: 'DRAFT',
  SCHEDULED: 'SCHEDULED',
  PUBLISHED: 'PUBLISHED',
  ARCHIVED: 'ARCHIVED',
} as const;
export type BlogStatus = (typeof BlogStatus)[keyof typeof BlogStatus];

/**
 * Editorial status of a post. Mirrors `BlogStatus` exactly, and is deliberately
 * unrelated to delivery state — a post can be PUBLISHED while one of its
 * targets is still FAILED. Never derive one from the other.
 */
export const PostStatus = {
  DRAFT: 'DRAFT',
  SCHEDULED: 'SCHEDULED',
  PUBLISHED: 'PUBLISHED',
  ARCHIVED: 'ARCHIVED',
} as const;
export type PostStatus = (typeof PostStatus)[keyof typeof PostStatus];

/**
 * Where a post's content came from. Only `BLOG` and `COMPOSER` are written
 * today; the rest exist so adding those sources later is a writer, not a
 * migration.
 */
export const PostSourceType = {
  BLOG: 'BLOG',
  COMPOSER: 'COMPOSER',
  RSS: 'RSS',
  BULK_IMPORT: 'BULK_IMPORT',
  AI_PROMPT: 'AI_PROMPT',
  /** Made in the YouTube section; edited there, not in the composer. */
  YOUTUBE: 'YOUTUBE',
} as const;
export type PostSourceType =
  (typeof PostSourceType)[keyof typeof PostSourceType];

/**
 * What kind of assertion the backend flagged in an AI-written draft.
 *
 * Mirrors `ClaimKind` in `src/utils/unverified-claims.util.ts`. A post written
 * from a topic has no source behind it, so the backend reports every checkable
 * claim the model introduced and the composer has to be able to name them.
 */
export const ClaimKind = {
  /** A percentage, ratio or multiplier. */
  STATISTIC: 'STATISTIC',
  MONEY: 'MONEY',
  DATE: 'DATE',
  /** A counted quantity large enough to be a claim rather than prose. */
  QUANTITY: 'QUANTITY',
  QUOTE: 'QUOTE',
  /** An appeal to a source — "studies show", "according to". */
  ATTRIBUTION: 'ATTRIBUTION',
  /** A ranking or a first/best/largest assertion. */
  SUPERLATIVE: 'SUPERLATIVE',
} as const;
export type ClaimKind = (typeof ClaimKind)[keyof typeof ClaimKind];

/**
 * Where a distribution target's text came from.
 *
 * The backend has no enum file for this — it is an inline union in
 * `distributions.service.ts` (`describeContentSource`), derived from
 * `generatedContent` and `edited` rather than stored. Mirrored here because a
 * target that reached READY without ever being generated is indistinguishable
 * from a bug unless the review screen labels it.
 */
export const ContentSource = {
  /** The caption was written by a person. Generation was skipped. */
  USER: 'USER',
  AI: 'AI',
  AI_EDITED: 'AI_EDITED',
  NONE: 'NONE',
} as const;
export type ContentSource = (typeof ContentSource)[keyof typeof ContentSource];

/**
 * How often a library posts to an account.
 *
 * A preset is a generator for `postingSlot` rows and the label shown instead of
 * them. Editing any generated slot moves the assignment to `CUSTOM` — the
 * backend enforces that, because a label describing a schedule that is no
 * longer there is worse than no label.
 */
export const CadencePreset = {
  DAILY: 'DAILY',
  WEEKDAYS: 'WEEKDAYS',
  /** Monday, Wednesday, Friday. Spelled out because an enum key cannot start with a digit. */
  THREE_X_WEEK: 'THREE_X_WEEK',
  CUSTOM: 'CUSTOM',
} as const;
export type CadencePreset = (typeof CadencePreset)[keyof typeof CadencePreset];

/**
 * Lifecycle of one planned posting.
 *
 * Not delivery state — that is `DistributionTargetStatus`. An entry is
 * DISPATCHED the moment a run has been created from it, whatever the platform
 * later does with that run.
 */
export const QueueEntryState = {
  PLANNED: 'PLANNED',
  DISPATCHED: 'DISPATCHED',
  CANCELLED: 'CANCELLED',
} as const;
export type QueueEntryState =
  (typeof QueueEntryState)[keyof typeof QueueEntryState];

/** Whether the filler placed an entry, or a person did. */
export const QueueEntrySource = {
  SLOT_FILL: 'SLOT_FILL',
  MANUAL: 'MANUAL',
} as const;
export type QueueEntrySource =
  (typeof QueueEntrySource)[keyof typeof QueueEntrySource];

export const ContentFormat = {
  MARKDOWN: 'MARKDOWN',
  HTML: 'HTML',
} as const;
export type ContentFormat = (typeof ContentFormat)[keyof typeof ContentFormat];

export const DistributionStatus = {
  PENDING: 'PENDING',
  GENERATING: 'GENERATING',
  AWAITING_APPROVAL: 'AWAITING_APPROVAL',
  SCHEDULED: 'SCHEDULED',
  POSTING: 'POSTING',
  COMPLETED: 'COMPLETED',
  PARTIALLY_FAILED: 'PARTIALLY_FAILED',
  FAILED: 'FAILED',
  CANCELLED: 'CANCELLED',
} as const;
export type DistributionStatus =
  (typeof DistributionStatus)[keyof typeof DistributionStatus];

export const DistributionTargetStatus = {
  PENDING: 'PENDING',
  GENERATING: 'GENERATING',
  READY: 'READY',
  APPROVED: 'APPROVED',
  POSTING: 'POSTING',
  POSTED: 'POSTED',
  FAILED: 'FAILED',
  SKIPPED: 'SKIPPED',
  CANCELLED: 'CANCELLED',
} as const;
export type DistributionTargetStatus =
  (typeof DistributionTargetStatus)[keyof typeof DistributionTargetStatus];

/** Statuses where the run is still moving and the UI should keep polling. */
export const ACTIVE_DISTRIBUTION_STATUSES: DistributionStatus[] = [
  DistributionStatus.PENDING,
  DistributionStatus.GENERATING,
  DistributionStatus.POSTING,
];

export const SocialAccountStatus = {
  ACTIVE: 'ACTIVE',
  EXPIRED: 'EXPIRED',
  RECONNECT_REQUIRED: 'RECONNECT_REQUIRED',
  DISABLED: 'DISABLED',
} as const;
export type SocialAccountStatus =
  (typeof SocialAccountStatus)[keyof typeof SocialAccountStatus];

export const MediaType = {
  IMAGE: 'IMAGE',
  VIDEO: 'VIDEO',
} as const;
export type MediaType = (typeof MediaType)[keyof typeof MediaType];

export const MediaVariant = {
  ORIGINAL: 'ORIGINAL',
  SQUARE: 'SQUARE',
  PORTRAIT: 'PORTRAIT',
  LANDSCAPE: 'LANDSCAPE',
  THUMBNAIL: 'THUMBNAIL',
} as const;
export type MediaVariant = (typeof MediaVariant)[keyof typeof MediaVariant];

/**
 * What an AI call produced. Text is billed per token, images per image, so
 * anything reporting cost has to keep the two apart.
 */
export const AiGenerationKind = {
  TEXT: 'TEXT',
  IMAGE: 'IMAGE',
} as const;
export type AiGenerationKind =
  (typeof AiGenerationKind)[keyof typeof AiGenerationKind];

/**
 * Third-party stock libraries the media picker can search.
 *
 * The values double as the `:provider` path segment on /stock/:provider/search.
 */
export const StockProvider = {
  UNSPLASH: 'UNSPLASH',
  PIXABAY: 'PIXABAY',
  GIPHY: 'GIPHY',
} as const;
export type StockProvider = (typeof StockProvider)[keyof typeof StockProvider];

/** Whether an asset was uploaded or generated. */
export const MediaSource = {
  UPLOAD: 'UPLOAD',
  AI_GENERATED: 'AI_GENERATED',
} as const;
export type MediaSource = (typeof MediaSource)[keyof typeof MediaSource];

/**
 * Generated image sizes. Square is the default — Instagram is the only
 * platform where media is mandatory, and the other crops are derived from
 * whatever is generated.
 */
export const ImageSize = {
  SQUARE: '1024x1024',
  PORTRAIT: '1024x1536',
  LANDSCAPE: '1536x1024',
} as const;
export type ImageSize = (typeof ImageSize)[keyof typeof ImageSize];

/** Quality tier. Roughly a 7x price spread from low to high on one model. */
export const ImageQuality = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
} as const;
export type ImageQuality = (typeof ImageQuality)[keyof typeof ImageQuality];

export const JobStatus = {
  PENDING: 'PENDING',
  RUNNING: 'RUNNING',
  SUCCEEDED: 'SUCCEEDED',
  DEAD: 'DEAD',
  CANCELLED: 'CANCELLED',
} as const;
export type JobStatus = (typeof JobStatus)[keyof typeof JobStatus];

/**
 * Which mechanism put something on the upcoming list. Mirrors the backend enum.
 *
 * Only a QUEUE row can be skipped or moved through the queue API — a
 * DISTRIBUTION row is a post scheduled in the composer, and its id is a
 * distribution target's, which those endpoints do not accept.
 */
export const UpcomingKind = {
  QUEUE: 'QUEUE',
  DISTRIBUTION: 'DISTRIBUTION',
} as const;
export type UpcomingKind = (typeof UpcomingKind)[keyof typeof UpcomingKind];

export const ActivityType = {
  BLOG_CREATED: 'BLOG_CREATED',
  BLOG_UPDATED: 'BLOG_UPDATED',
  BLOG_DELETED: 'BLOG_DELETED',
  BLOG_PUBLISHED: 'BLOG_PUBLISHED',
  BLOG_ARCHIVED: 'BLOG_ARCHIVED',
  POST_CREATED: 'POST_CREATED',
  POST_UPDATED: 'POST_UPDATED',
  POST_DELETED: 'POST_DELETED',
  /** Released from drafting. Editorial — not proof anything reached a platform. */
  POST_PUBLISHED: 'POST_PUBLISHED',
  SOCIAL_ACCOUNT_CONNECTED: 'SOCIAL_ACCOUNT_CONNECTED',
  SOCIAL_ACCOUNT_DISCONNECTED: 'SOCIAL_ACCOUNT_DISCONNECTED',
  /** Erased on a platform data deletion request — the row is gone, not hidden. */
  SOCIAL_ACCOUNT_PURGED: 'SOCIAL_ACCOUNT_PURGED',
  SOCIAL_ACCOUNT_RECONNECT_REQUIRED: 'SOCIAL_ACCOUNT_RECONNECT_REQUIRED',
  PLATFORM_PRESET_UPDATED: 'PLATFORM_PRESET_UPDATED',
  DISTRIBUTION_CREATED: 'DISTRIBUTION_CREATED',
  DISTRIBUTION_CANCELLED: 'DISTRIBUTION_CANCELLED',
  DISTRIBUTION_TARGET_EDITED: 'DISTRIBUTION_TARGET_EDITED',
  DISTRIBUTION_TARGET_APPROVED: 'DISTRIBUTION_TARGET_APPROVED',
  DISTRIBUTION_TARGET_REGENERATED: 'DISTRIBUTION_TARGET_REGENERATED',
  DISTRIBUTION_TARGET_POSTED: 'DISTRIBUTION_TARGET_POSTED',
  DISTRIBUTION_TARGET_FAILED: 'DISTRIBUTION_TARGET_FAILED',
  DISTRIBUTION_TARGET_RETRIED: 'DISTRIBUTION_TARGET_RETRIED',
  /** The queue filler reached a slot and had nothing eligible to put in it. */
  QUEUE_SLOT_UNFILLED: 'QUEUE_SLOT_UNFILLED',
  MEDIA_UPLOADED: 'MEDIA_UPLOADED',
  MEDIA_DELETED: 'MEDIA_DELETED',
} as const;
export type ActivityType = (typeof ActivityType)[keyof typeof ActivityType];

/** A member's rank within one workspace. Mirrors the backend enum. */
export const WorkspaceRole = {
  OWNER: 'OWNER',
  ADMIN: 'ADMIN',
  MEMBER: 'MEMBER',
} as const;
export type WorkspaceRole = (typeof WorkspaceRole)[keyof typeof WorkspaceRole];

/** Whether a row in the members table is a real member or a pending invite. */
export const MemberStatus = {
  ACTIVE: 'ACTIVE',
  INVITED: 'INVITED',
} as const;
export type MemberStatus = (typeof MemberStatus)[keyof typeof MemberStatus];

/**
 * Where a post's link goes, per platform.
 *
 * Mirrors `src/enums/link-placement.enum.ts`. Note that `IN_FIRST_REPLY` is not
 * the cheap option, however it reads: X bills $0.200 for any post whose text
 * contains a URL and a reply is another post, so it costs $0.015 *more* than
 * putting the link in the body. It buys reach — X ranks link posts lower — not
 * savings. `NONE` is the only setting that saves money.
 */
export const LinkPlacement = {
  IN_POST: 'IN_POST',
  IN_FIRST_REPLY: 'IN_FIRST_REPLY',
  NONE: 'NONE',
} as const;
export type LinkPlacement = (typeof LinkPlacement)[keyof typeof LinkPlacement];

/* ─── Billing ────────────────────────────────────────────────────────────── */

/** The pricing tiers. Mirrors `src/enums/plan.enum.ts`. */
export const PlanTier = {
  TRIAL: 'TRIAL',
  STARTER: 'STARTER',
  PRO: 'PRO',
  AGENCY: 'AGENCY',
} as const;
export type PlanTier = (typeof PlanTier)[keyof typeof PlanTier];

/**
 * Where a subscription is in its life.
 *
 * Two of these are counter-intuitive and the UI must not "helpfully" treat them
 * as lapsed: `PAST_DUE` is still entitled while the gateway retries a declined
 * card, and `CANCELED` is still entitled until its period actually ends.
 * `COMPED` is entitled with no expiry at all.
 */
export const SubscriptionStatus = {
  TRIALING: 'TRIALING',
  ACTIVE: 'ACTIVE',
  PAST_DUE: 'PAST_DUE',
  CANCELED: 'CANCELED',
  EXPIRED: 'EXPIRED',
  COMPED: 'COMPED',
} as const;
export type SubscriptionStatus =
  (typeof SubscriptionStatus)[keyof typeof SubscriptionStatus];

/** Who took the money. Null on a trial and on a comped workspace. */
export const BillingGateway = {
  STRIPE: 'STRIPE',
  SSLCOMMERZ: 'SSLCOMMERZ',
} as const;
export type BillingGateway =
  (typeof BillingGateway)[keyof typeof BillingGateway];

/** Where one payment attempt ended up. */
export const PaymentStatus = {
  PENDING: 'PENDING',
  PAID: 'PAID',
  FAILED: 'FAILED',
  REFUNDED: 'REFUNDED',
} as const;
export type PaymentStatus = (typeof PaymentStatus)[keyof typeof PaymentStatus];

/**
 * How a payment reads to the person who made it.
 *
 * `PENDING` is the one worth spelling out. It is not a failure and it is not a
 * plan — it is the few seconds between the gateway taking the money and our
 * server confirming it server-to-server, and "PENDING" in an enum voice reads
 * as something stuck. "Processing" says the same thing without the alarm.
 */
export const PAYMENT_STATUS_LABELS: Record<PaymentStatus, string> = {
  PENDING: 'Processing',
  PAID: 'Paid',
  FAILED: 'Failed',
  REFUNDED: 'Refunded',
};

/**
 * The gateway names, as a customer would recognise them.
 *
 * Stripe is **not** labelled "Card". SSLCommerz takes cards too, so the word
 * names nothing and implied a distinction that does not exist. What actually
 * separates them is that Stripe renews monthly in USD and SSLCommerz sells a
 * pre-paid block in BDT — see `BILLING_GATEWAY_TERMS`.
 */
export const BILLING_GATEWAY_LABELS: Record<BillingGateway, string> = {
  STRIPE: 'Stripe',
  SSLCOMMERZ: 'SSLCommerz',
};

/**
 * The one-line difference between the two gateways, for a chooser.
 *
 * Kept beside the names because the names alone do not tell anybody which to
 * pick, and the real question — does this renew on its own? — is answered
 * nowhere else on the screen.
 */
export const BILLING_GATEWAY_TERMS: Record<BillingGateway, string> = {
  STRIPE: 'renews monthly · USD',
  SSLCOMMERZ: 'one payment · BDT',
};

/**
 * What a workspace is capped on. Mirrors `src/enums/usage-metric.enum.ts`.
 *
 * Split by shape rather than by subject: flows accumulate over a period and
 * reset, stocks are current state with no period. The billing screen renders
 * them differently for that reason — a flow has a reset date and a stock does
 * not.
 */
export const UsageMetric = {
  POSTS_PUBLISHED: 'POSTS_PUBLISHED',
  AI_TEXT_CALLS: 'AI_TEXT_CALLS',
  AI_IMAGES: 'AI_IMAGES',
  X_LINK_POSTS: 'X_LINK_POSTS',
  X_STANDARD_POSTS: 'X_STANDARD_POSTS',
  CONNECTED_ACCOUNTS: 'CONNECTED_ACCOUNTS',
  LIBRARY_POSTS: 'LIBRARY_POSTS',
  SEATS: 'SEATS',
  STORAGE_BYTES: 'STORAGE_BYTES',
} as const;
export type UsageMetric = (typeof UsageMetric)[keyof typeof UsageMetric];

/** The bucket a flow accumulates into, in the workspace's own timezone. */
export const UsagePeriod = {
  DAY: 'DAY',
  MONTH: 'MONTH',
} as const;
export type UsagePeriod = (typeof UsagePeriod)[keyof typeof UsagePeriod];

/**
 * What to call each metric on screen.
 *
 * Phrased as a noun the user recognises rather than the enum name. "X posts
 * containing a link" is deliberately spelled out: it is the expensive kind, it
 * is capped separately, and nobody would guess that from a shorter label.
 */
export const USAGE_METRIC_LABELS: Record<UsageMetric, string> = {
  POSTS_PUBLISHED: 'Published posts',
  AI_TEXT_CALLS: 'AI writing calls',
  AI_IMAGES: 'AI images',
  X_LINK_POSTS: 'X posts with a link',
  X_STANDARD_POSTS: 'X posts',
  CONNECTED_ACCOUNTS: 'Connected accounts',
  LIBRARY_POSTS: 'Posts',
  SEATS: 'Team seats',
  STORAGE_BYTES: 'Storage',
};

/** Statuses in which the workspace may still spend. */
export const ENTITLED_STATUSES: SubscriptionStatus[] = [
  SubscriptionStatus.TRIALING,
  SubscriptionStatus.ACTIVE,
  SubscriptionStatus.PAST_DUE,
  SubscriptionStatus.CANCELED,
  SubscriptionStatus.COMPED,
];

/**
 * How one platform gets its text, while a post is being composed.
 *
 * The mode is the control, not a guess made from what happens to be typed:
 * skipping AI is a real decision with a real consequence, so it is chosen
 * rather than inferred from an empty box.
 *
 * - `SHARED` — post the shared text exactly as written. No AI, no spend.
 * - `AI`     — the model writes this platform's version.
 * - `SELF`   — write this platform's own text by hand.
 *
 * Lives here rather than in `composerPlan.ts` because it is now part of the
 * wire contract (`PostPlatformVersion`), and the API types must not depend on
 * a component module. `composerPlan` re-exports it, so existing imports are
 * unchanged.
 */
export type TailoringMode = 'SHARED' | 'AI' | 'SELF';

/**
 * How one platform treats the post's shared first comment. `null` inherits:
 * the shared comment, else the workspace's preset default. Mirrors
 * `src/enums/first-comment.enum.ts`.
 */
export const FirstCommentMode = {
  CUSTOM: 'CUSTOM',
  OFF: 'OFF',
} as const;
export type FirstCommentMode =
  (typeof FirstCommentMode)[keyof typeof FirstCommentMode];

/** Where a first comment stands — its own lifecycle, apart from the post's. */
export const FirstCommentStatus = {
  PENDING: 'PENDING',
  POSTING: 'POSTING',
  POSTED: 'POSTED',
  FAILED: 'FAILED',
  SKIPPED: 'SKIPPED',
} as const;
export type FirstCommentStatus =
  (typeof FirstCommentStatus)[keyof typeof FirstCommentStatus];
